import { ColDef } from 'ag-grid-community';
import { Dictionary, startCase } from 'lodash';
import { UseMutateAsyncFunction } from 'react-query';
import { User } from 'api/models';
import { FieldValueType } from 'api/tasks';
import { toRowNodeComparator } from 'components/atoms/grid/plugins/with-columns';
import { getCellEditorPopupPosition } from 'components/atoms/grid/utils';
import {
  memberNameSorter,
  permissionSorter,
} from 'components/organisms/module-settings/team-members/list/table-utils';
import { getEditor } from 'components/organisms/tasks/editors';
import { FieldType, FilterableField } from 'components/organisms/tasks/fields';
import {
  getValueFormatter,
  getRenderer as getRendererBase,
} from 'components/organisms/tasks/renderers';
import {
  suppressEnterAndTabKey,
  suppressEnterKey,
} from 'components/organisms/tasks/utils';
import { Member } from 'data/members/api';
import type { Translate } from 'hooks/use-translation';
import { PartialMember } from 'queries/members';
import { getFieldMapReducer } from 'routes/projects/list-v2/transformer';
import { TaskField } from 'stores/fields';
import { getBaseComparator } from 'utils/comparators/base-comparator';
import { UserFieldsName } from 'utils/user-fields';
import { getBaseValueGetter } from 'utils/value-getter/base-value-getter';

const FIRST_COLUMN = UserFieldsName.UserId;
const LAST_COLUMN = 'moreOptions';

const nonEditableFields = [
  UserFieldsName.UserId,
  UserFieldsName.EmailId,
  UserFieldsName.PermissionSetId,
  UserFieldsName.WeeklyCapacity,
  UserFieldsName.CostRate,
  UserFieldsName.UserStatus,
];

const defaultColumnOrder = [
  UserFieldsName.UserId,
  UserFieldsName.EmailId,
  UserFieldsName.UserStatus,
];

const getRenderer = (field: FilterableField) => {
  switch (field.fieldName) {
    case UserFieldsName.EmailId:
      return 'email';
    case UserFieldsName.UserStatus:
      return 'Status';
    case UserFieldsName.UserId:
      return 'GetUserName';
    case UserFieldsName.PermissionSetId:
      return 'getPermission';
    case UserFieldsName.CostRate:
      return 'GetCostRate';
    default:
      return getRendererBase(field);
  }
};

const getValueGetter = (field: FilterableField) => {
  return getBaseValueGetter(field);
};

const getComparator = (field: FilterableField) => {
  switch (field.fieldName) {
    case UserFieldsName.UserId:
      return (_valA, _valB, nodeA, nodeB) =>
        memberNameSorter(nodeA.data.user, nodeB.data.user);
    case UserFieldsName.PermissionSetId:
      return (_valA, _valB, nodeA, nodeB) =>
        permissionSorter(nodeA.data.permissionSet, nodeB.data.permissionSet);
    default:
      return toRowNodeComparator(getBaseComparator(field));
  }
};

export const getColumnDef = (
  field: FilterableField,
  translate: Translate,
  memberships,
): ColDef => {
  return {
    field: field.fieldName,
    headerName: field.fieldLabel ?? startCase(field.fieldName),
    headerValueGetter: (params) => {
      if (params.column?.getColId() === FIRST_COLUMN) {
        params.api.refreshCells();

        return translate('team_members');
      }

      return field.fieldLabel ?? startCase(field.fieldName);
    },
    cellRenderer: getRenderer(field),
    sortable: true,
    sort: field.fieldName === FIRST_COLUMN ? 'asc' : undefined,
    cellRendererParams: { translate, memberships },
    valueGetter: getValueGetter(field),
    valueFormatter: getValueFormatter(field),
    comparator: getComparator(field),
    lockPosition: field.fieldName === FIRST_COLUMN,
    pinned: field.fieldName === FIRST_COLUMN,
    autoSizeStrategy: {
      type: 'fitGridWidth',
    },
    width: [FIRST_COLUMN].includes(field.fieldName) ? 360 : 200,
    cellStyle: (params) => {
      if (params.colDef.field === FIRST_COLUMN) {
        return { borderRight: '1px solid var(--table-border-color)' };
      }

      return undefined;
    },
    suppressKeyboardEvent:
      field.type === FieldType.MultiLineText
        ? suppressEnterAndTabKey
        : suppressEnterKey,
  } as ColDef;
};

export const withEditFields = (
  field: FilterableField,
  isDeactivatedTab: boolean,
  isTableCoulmnEditable: boolean,
): Pick<
  ColDef,
  'editable' | 'cellClass' | 'cellEditor' | 'cellEditorPopupPosition'
> => {
  const editable =
    isDeactivatedTab || !isTableCoulmnEditable
      ? false
      : !nonEditableFields.includes(field.fieldName);
  const cellEditor = editable ? getEditor(field as TaskField) : null;

  const cellClass = () =>
    editable || field.fieldName === FIRST_COLUMN
      ? ['grid-cell-editable']
      : ['grid-cell-not-editable'];

  return {
    editable,
    cellClass,
    cellEditor,
    cellEditorPopupPosition: getCellEditorPopupPosition(field),
  };
};

export const toColDef =
  (
    translate: Translate,
    isDeactivatedTab: boolean,
    isTableCoulmnEditable: boolean,
    memberships,
  ) =>
  (fields: FilterableField[]): ColDef[] => {
    return fields.map((field) => ({
      ...getColumnDef(field, translate, memberships),
      ...withEditFields(field, isDeactivatedTab, isTableCoulmnEditable),
    }));
  };

export const withMoreActionsColumn =
  (project, loggedInUserPermissions, membersMeta, loggedInUser, memberships) =>
  (columns: ColDef[]): ColDef[] => {
    return [
      ...columns,
      {
        field: LAST_COLUMN,
        headerName: '',
        sortable: false,
        suppressMenu: true,
        lockPosition: true,
        pinned: 'right',
        suppressColumnsToolPanel: true,
        width: 80,
        cellClass: ['grid-cell-editable'],
        cellStyle: { borderLeft: '1px solid var(--table-border-color)' },
        cellRendererParams: {
          project,
          loggedInUserPermissions,
          membersMeta,
          loggedInUser,
          memberships,
        },
        cellRenderer: 'MoreActionsCellRenderer',
      },
    ];
  };

export const getUserPayload = (
  user: User,
  field: FilterableField,
  newValue: FieldValueType,
): {
  userId: number;
  fields: { fieldId: number; fieldValue: FieldValueType }[];
} => {
  const fields = user?.fields ?? [];

  return {
    userId: user.userId,
    fields: [
      ...fields,
      {
        fieldId: Number(field.fieldId),
        fieldValue: newValue,
      },
    ],
  };
};

export const updateFn =
  (mutateAsync: UseMutateAsyncFunction<User, unknown, unknown, unknown>) =>
  (
    { user },
    field: FilterableField,
    from: FieldValueType = null,
    to: FieldValueType = null,
  ) => {
    if (from === to) {
      return;
    }
    const userPayload = getUserPayload(user, field, to);

    if (!userPayload) {
      return;
    }

    mutateAsync({ user: userPayload });
  };

export const withOnUpdate =
  (mutateAsync: UseMutateAsyncFunction<User, unknown, unknown, unknown>) =>
  (columns: ColDef[]): ColDef[] => {
    return columns.map((col) => ({
      ...col,
      cellEditorParams: {
        onUpdate: updateFn(mutateAsync),
      },
    }));
  };

export const relativelySortByDefaultColumnOrder = (
  source: ColDef[],
): ColDef[] => {
  const order = Object.fromEntries(defaultColumnOrder.map((v, i) => [v, i]));

  return Array.from(source).sort(
    (a, b) =>
      (order[a.field!] ?? Number.MAX_VALUE) -
      (order[b.field!] ?? Number.MAX_VALUE),
  );
};

export const transformMemberData = (
  fieldLookup: Dictionary<FilterableField>,
  data,
) => {
  const toFieldMap = getFieldMapReducer(fieldLookup);

  const newData = data?.reduce((list, { fields, ...rest }) => {
    const fieldsMap = (fields ?? []).reduce(toFieldMap, {});

    return [
      ...list,
      {
        ...rest,
        ...fieldsMap,
        user: { ...rest, fields },
      },
    ];
  }, [] as Member[]);

  return newData;
};

export const transformSelectedMemberData = (data) => {
  const newData = data?.reduce((list, curr) => {
    return [
      ...list,
      {
        ...curr.user,
        user: curr.user,
        permissionSet: curr.permissionSet,
      },
    ];
  }, [] as PartialMember[]);

  return newData;
};
